import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  Database, 
  Edit, 
  Trash2, 
  Play,
  Send,
  Bot,
  User,
  Sparkles,
  Code,
  Eye,
  Save,
  X,
  Loader2,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const DatasetsPage = () => {
  const [datasets, setDatasets] = useState([]);
  const [dbConnections, setDbConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [builderMode, setBuilderMode] = useState('ai'); // 'ai' or 'manual'
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingDataset, setDeletingDataset] = useState(null);

  // Form state
  const [datasetForm, setDatasetForm] = useState({
    name: '',
    db_connection_id: '',
    sql_query: '',
    description: ''
  });

  // AI Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const chatEndRef = useRef(null);

  // Preview state
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [datasetsRes, connectionsRes] = await Promise.all([
        apiService.getDatasets(),
        apiService.getDBConnections()
      ]);
      setDatasets(datasetsRes.data);
      setDbConnections(connectionsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openBuilder = (dataset = null) => {
    const newSessionId = `dataset_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    setChatMessages([{
      role: 'assistant',
      content: "Hello! I'm your AI assistant for building SQL queries. What kind of data do you need to fetch? Tell me about:\n\n• The type of records (sales, purchases, ledgers, etc.)\n• Any date range requirements\n• Specific filters or conditions\n\nI'll help you build the perfect SQL query step by step!"
    }]);
    
    if (dataset) {
      setSelectedDataset(dataset);
      setDatasetForm({
        name: dataset.name,
        db_connection_id: dataset.db_connection_id,
        sql_query: dataset.sql_query,
        description: dataset.description || ''
      });
    } else {
      setSelectedDataset(null);
      setDatasetForm({
        name: '',
        db_connection_id: dbConnections[0]?.id || '',
        sql_query: '',
        description: ''
      });
    }
    setPreviewData(null);
    setIsBuilderOpen(true);
  };

  const closeBuilder = () => {
    setIsBuilderOpen(false);
    setSelectedDataset(null);
    setChatMessages([]);
    setChatInput('');
    setPreviewData(null);
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    if (!datasetForm.db_connection_id) {
      toast.error('Please select a MySQL connection first');
      return;
    }

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const response = await apiService.sendAIMessage({
        session_id: sessionId,
        message: userMessage,
        context_type: 'sql',
        db_connection_id: datasetForm.db_connection_id
      });

      const aiResponse = response.data;
      setChatMessages(prev => [...prev, { role: 'assistant', content: aiResponse.response }]);

      // If SQL was generated, update the form
      if (aiResponse.sql_query) {
        setDatasetForm(prev => ({ ...prev, sql_query: aiResponse.sql_query }));
        toast.success('SQL query generated! Review and save when ready.');
      }
    } catch (error) {
      toast.error('AI service error. Please try again.');
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again or write your SQL manually.' 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!datasetForm.sql_query.trim()) {
      toast.error('Please enter a SQL query');
      return;
    }
    if (!datasetForm.db_connection_id) {
      toast.error('Please select a MySQL connection');
      return;
    }

    // Check for dangerous queries
    const dangerousKeywords = ['DELETE', 'UPDATE', 'DROP', 'INSERT', 'ALTER', 'TRUNCATE'];
    const upperQuery = datasetForm.sql_query.toUpperCase();
    const hasDangerous = dangerousKeywords.some(kw => upperQuery.includes(kw));
    
    if (hasDangerous) {
      toast.error('Query contains potentially dangerous operations. Only SELECT queries are allowed.');
      return;
    }

    setPreviewLoading(true);
    try {
      // First save the dataset temporarily
      let datasetId = selectedDataset?.id;
      
      if (!datasetId) {
        // Create temporary dataset
        const tempName = datasetForm.name || `temp_${Date.now()}`;
        const createRes = await apiService.createDataset({
          ...datasetForm,
          name: tempName
        });
        datasetId = createRes.data.id;
        setSelectedDataset(createRes.data);
        if (!datasetForm.name) {
          setDatasetForm(prev => ({ ...prev, name: tempName }));
        }
      } else {
        // Update existing
        await apiService.updateDataset(datasetId, datasetForm);
      }

      const response = await apiService.previewDataset(datasetId, 20);
      setPreviewData(response.data);
      toast.success(`Preview loaded: ${response.data.count} rows`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed');
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (!datasetForm.name.trim()) {
      toast.error('Please enter a dataset name');
      return;
    }
    if (!datasetForm.db_connection_id) {
      toast.error('Please select a MySQL connection');
      return;
    }
    if (!datasetForm.sql_query.trim()) {
      toast.error('Please enter a SQL query');
      return;
    }

    try {
      if (selectedDataset) {
        await apiService.updateDataset(selectedDataset.id, datasetForm);
        toast.success('Dataset updated');
      } else {
        await apiService.createDataset(datasetForm);
        toast.success('Dataset created');
      }
      loadData();
      closeBuilder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save dataset');
    }
  };

  const openDeleteDialog = (dataset) => {
    setDeletingDataset(dataset);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingDataset) return;
    
    try {
      await apiService.deleteDataset(deletingDataset.id);
      toast.success('Dataset deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete dataset');
    } finally {
      setDeleteDialogOpen(false);
      setDeletingDataset(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // Builder View
  if (isBuilderOpen) {
    return (
      <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="dataset-builder">
        {/* Builder Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={closeBuilder}>
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <h2 className="font-heading font-semibold text-lg">
              {selectedDataset ? 'Edit Dataset' : 'New Dataset'}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-slate-100 rounded-lg p-1">
              <Button
                variant={builderMode === 'ai' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setBuilderMode('ai')}
                className={builderMode === 'ai' ? 'bg-teal-700 hover:bg-teal-600' : ''}
                data-testid="ai-mode-btn"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                AI Chat
              </Button>
              <Button
                variant={builderMode === 'manual' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setBuilderMode('manual')}
                className={builderMode === 'manual' ? 'bg-teal-700 hover:bg-teal-600' : ''}
                data-testid="manual-mode-btn"
              >
                <Code className="h-4 w-4 mr-2" />
                Manual
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreview}
              disabled={previewLoading}
              data-testid="preview-btn"
            >
              {previewLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Eye className="h-4 w-4 mr-2" />
              )}
              Preview
            </Button>
            <Button
              className="bg-teal-700 hover:bg-teal-600"
              size="sm"
              onClick={handleSave}
              data-testid="save-dataset-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Dataset
            </Button>
          </div>
        </div>

        {/* Builder Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - AI Chat or Manual */}
          <div className="w-2/5 border-r border-slate-200 flex flex-col bg-white">
            {/* Dataset Config */}
            <div className="p-4 border-b border-slate-200 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Dataset Name</Label>
                  <Input
                    placeholder="e.g., Sales Data Q4"
                    value={datasetForm.name}
                    onChange={(e) => setDatasetForm({ ...datasetForm, name: e.target.value })}
                    className="h-9"
                    data-testid="dataset-name-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">MySQL Connection</Label>
                  <Select
                    value={datasetForm.db_connection_id}
                    onValueChange={(value) => setDatasetForm({ ...datasetForm, db_connection_id: value })}
                  >
                    <SelectTrigger className="h-9" data-testid="mysql-connection-select">
                      <SelectValue placeholder="Select connection" />
                    </SelectTrigger>
                    <SelectContent>
                      {dbConnections.map((conn) => (
                        <SelectItem key={conn.id} value={conn.id}>
                          {conn.name} ({conn.database})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Description (Optional)</Label>
                <Input
                  placeholder="Brief description of this dataset"
                  value={datasetForm.description}
                  onChange={(e) => setDatasetForm({ ...datasetForm, description: e.target.value })}
                  className="h-9"
                />
              </div>
            </div>

            {builderMode === 'ai' ? (
              /* AI Chat Interface */
              <>
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {chatMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[85%] ${
                          msg.role === 'user' 
                            ? 'chat-bubble-user' 
                            : 'chat-bubble-ai'
                        }`}>
                          <div className="flex items-start gap-2">
                            {msg.role === 'assistant' && (
                              <Bot className="h-4 w-4 mt-0.5 text-teal-600 flex-shrink-0" />
                            )}
                            <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                            {msg.role === 'user' && (
                              <User className="h-4 w-4 mt-0.5 text-slate-500 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {chatLoading && (
                      <div className="flex justify-start">
                        <div className="chat-bubble-ai">
                          <div className="flex items-center gap-2">
                            <Bot className="h-4 w-4 text-teal-600" />
                            <Loader2 className="h-4 w-4 animate-spin text-teal-600" />
                            <span className="text-sm text-slate-500">Thinking...</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                </ScrollArea>
                <div className="p-4 border-t border-slate-200">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Ask me to generate SQL... e.g., 'Get all sales from last month'"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                      disabled={chatLoading}
                      className="flex-1"
                      data-testid="chat-input"
                    />
                    <Button
                      className="bg-teal-700 hover:bg-teal-600"
                      onClick={sendChatMessage}
                      disabled={chatLoading || !chatInput.trim()}
                      data-testid="send-chat-btn"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              /* Manual SQL Editor */
              <div className="flex-1 p-4 flex flex-col">
                <Label className="text-xs mb-2">SQL Query</Label>
                <Textarea
                  placeholder="SELECT * FROM your_table WHERE condition = 'value'"
                  value={datasetForm.sql_query}
                  onChange={(e) => setDatasetForm({ ...datasetForm, sql_query: e.target.value })}
                  className="flex-1 font-mono text-sm resize-none"
                  data-testid="sql-textarea"
                />
                <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                  <AlertTriangle className="h-3 w-3" />
                  Only SELECT queries are allowed for safety
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - SQL Editor & Preview */}
          <div className="flex-1 flex flex-col bg-slate-50">
            {/* Generated SQL */}
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-medium">Generated SQL Query</Label>
                {datasetForm.sql_query && (
                  <Badge className="badge-success">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Ready
                  </Badge>
                )}
              </div>
              <div className="code-editor max-h-40 overflow-auto">
                <pre className="text-sm">{datasetForm.sql_query || '-- SQL query will appear here'}</pre>
              </div>
            </div>

            {/* Preview Results */}
            <div className="flex-1 p-4 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-xs font-medium">Data Preview</Label>
                {previewData && (
                  <span className="text-xs text-slate-500">
                    Showing {previewData.count} of {previewData.count}+ rows
                  </span>
                )}
              </div>
              <Card className="flex-1 overflow-hidden">
                <CardContent className="p-0 h-full">
                  {previewData ? (
                    <ScrollArea className="h-full">
                      <Table>
                        <TableHeader>
                          <TableRow className="table-header">
                            {previewData.columns.map((col) => (
                              <TableHead key={col} className="table-cell font-mono text-xs">
                                {col}
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {previewData.rows.map((row, idx) => (
                            <TableRow key={idx} className="table-row">
                              {previewData.columns.map((col) => (
                                <TableCell key={col} className="table-cell text-xs font-mono">
                                  {row[col]?.toString() || '-'}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-500">
                      <div className="text-center">
                        <Database className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>Click "Preview" to see data</p>
                        <p className="text-sm">Make sure you have a SQL query ready</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="page-container" data-testid="datasets-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Datasets</h1>
          <p className="text-sm text-slate-500 mt-1">Create and manage SQL datasets for Tally integration</p>
        </div>
        <Button 
          className="bg-teal-700 hover:bg-teal-600"
          onClick={() => openBuilder()}
          disabled={dbConnections.length === 0}
          data-testid="create-dataset-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Dataset
        </Button>
      </div>

      {dbConnections.length === 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">No MySQL Connections</p>
                <p className="text-sm text-amber-700">Please add a MySQL connection first before creating datasets.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead className="table-cell">Name</TableHead>
                <TableHead className="table-cell">Connection</TableHead>
                <TableHead className="table-cell">Columns</TableHead>
                <TableHead className="table-cell">Version</TableHead>
                <TableHead className="table-cell">Last Updated</TableHead>
                <TableHead className="table-cell text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {datasets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                    <Database className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No datasets created yet</p>
                    <p className="text-sm">Click "Create Dataset" to build your first SQL query</p>
                  </TableCell>
                </TableRow>
              ) : (
                datasets.map((dataset) => {
                  const connection = dbConnections.find(c => c.id === dataset.db_connection_id);
                  return (
                    <TableRow key={dataset.id} className="table-row" data-testid={`dataset-row-${dataset.id}`}>
                      <TableCell className="table-cell">
                        <div>
                          <p className="font-medium">{dataset.name}</p>
                          {dataset.description && (
                            <p className="text-xs text-slate-500 truncate max-w-xs">{dataset.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="table-cell">
                        <Badge className="badge-neutral">
                          {connection?.name || 'Unknown'}
                        </Badge>
                      </TableCell>
                      <TableCell className="table-cell">
                        {dataset.columns?.length || 0} columns
                      </TableCell>
                      <TableCell className="table-cell">
                        v{dataset.version}
                      </TableCell>
                      <TableCell className="table-cell text-sm text-slate-500">
                        {format(parseISO(dataset.updated_at), 'MMM d, yyyy HH:mm')}
                      </TableCell>
                      <TableCell className="table-cell text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openBuilder(dataset)}
                            data-testid={`edit-dataset-${dataset.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openDeleteDialog(dataset)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-dataset-${dataset.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Dataset</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingDataset?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              variant="destructive"
              onClick={handleDelete}
              data-testid="confirm-delete-dataset-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DatasetsPage;
